import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CHECKOUT } from "@/constants/testIds";
import { ZAR, useCart } from "@/lib/cart";
import { CheckCircle } from "@phosphor-icons/react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const order_id = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [marking, setMarking] = useState(false);
  const cart = useCart();

  const fetchOrder = async () => {
    if (!order_id) return;
    const { data } = await api.get(`/orders/${order_id}`);
    setOrder(data);
  };

  useEffect(() => {
    fetchOrder();
    const t = setInterval(fetchOrder, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id]);

  // Clear the cart once we have confirmation the order was created.
  useEffect(() => {
    if (order && cart.count > 0) cart.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.order_id]);

  // Fire Purchase event once we know payment succeeded — only once per order.
  useEffect(() => {
    if (!order || order.status !== "paid") return;
    const key = `ve_purchase_tracked_${order.order_id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    try {
      const value = order.amount || 0;
      if (typeof window.gtag === "function") {
        window.gtag("event", "purchase", {
          transaction_id: order.order_id,
          value,
          currency: "ZAR",
          items: (order.items || []).map((it) => ({
            item_id: it.product_slug,
            item_name: it.name,
            price: it.unit_price,
            quantity: it.quantity,
          })),
        });
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "Purchase", {
          value,
          currency: "ZAR",
          content_ids: (order.items || []).map((i) => i.product_slug),
          num_items: (order.items || []).reduce((s, i) => s + i.quantity, 0),
        });
      }
    } catch {
      /* swallow */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  const forcePaid = async () => {
    setMarking(true);
    try {
      await api.post(`/payfast/mark-paid/${order_id}`);
      await fetchOrder();
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="mx-auto max-w-[900px] px-6 py-32 text-center lg:px-10">
      <CheckCircle size={72} weight="duotone" className="mx-auto text-[#00FFC2]" />
      <div data-testid={CHECKOUT.successMsg} className="font-label mt-6 text-xs text-[#00FFC2]">
        / PAYMENT COMPLETE
      </div>
      <h1 className="mt-3 font-display text-5xl font-black tracking-tighter">Thank you.</h1>
      <p className="mt-4 text-white/60">
        Order <span className="text-white">{order_id}</span> is on its way.
      </p>

      {order && (
        <div className="mt-12 border border-white/10 bg-[#0d0d0d] p-8 text-left">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="font-label text-[10px] text-white/40">STATUS</div>
              <div className="mt-1 font-display text-lg capitalize">{order.status}</div>
            </div>
            <div>
              <div className="font-label text-[10px] text-white/40">TOTAL</div>
              <div className="mt-1 font-display text-lg text-[#00FFC2]">{ZAR(order.amount)}</div>
            </div>
            <div>
              <div className="font-label text-[10px] text-white/40">EMAIL</div>
              <div className="mt-1 text-sm">{order.customer_email}</div>
            </div>
            <div>
              <div className="font-label text-[10px] text-white/40">POINTS EARNED</div>
              <div className="mt-1 text-sm text-[#FFC900]">+{order.points_earned}</div>
            </div>
          </div>
          {order.status !== "paid" && (
            <div className="mt-8 border-t border-white/10 pt-6 text-xs text-white/50">
              Waiting for PayFast confirmation... Sandbox ITN can take a moment.
              <button
                onClick={forcePaid}
                disabled={marking}
                className="ml-3 border border-white/20 px-3 py-1 font-label text-[10px] hover:border-[#FFC900] hover:text-[#FFC900]"
              >
                {marking ? "Marking..." : "Mark as paid (sandbox)"}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-12 flex justify-center gap-3">
        <Link to="/shop" className="border border-white/20 px-6 py-3 font-label text-xs hover:border-white/50 transition-colors">
          Continue shopping
        </Link>
        <Link
          to={`/track?order_id=${order_id}&email=${order?.customer_email || ""}`}
          className="border border-white/20 px-6 py-3 font-label text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
        >
          Track order
        </Link>
        <Link to="/account" className="bg-[#FF5A1F] px-6 py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform">
          View orders
        </Link>
      </div>
    </div>
  );
}
